import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useEthers } from '@usedapp/core';
import { ChainID, supportedChain } from '../types/ChainID'
import { Coupon, ERC20, HedgeyAdapter, Issuer, Multicall3, Test, TestnetFaucet, TilterFactory, TokenLockupPlans, UniPriceFetcher, UniswapV2Factory, UniswapV2Router02 } from "../typechain/types/ethers";
import useAddresses from '../hooks/useAddresses'; // Updated import for renamed hook
import { useContracts } from '../hooks/useContracts';
import { BigNumber } from 'ethers';
import _, { add } from 'lodash';
import { DynamicTokenInfo, useDynamicTokenInfo } from '../hooks/useDynamicTokenInfo';
import { isTiltingTokenFactory } from '../extensions/Utils';
import useInputPrices from '../hooks/useInputPrices';
import { useAnchorPrices } from '../hooks/useAnchorPrices';
import { useTokenLockupConfig } from '../hooks/useTokenLockupConfig';
import { defaultRewardConfig, RewardConfig, useRewardConfig } from '../hooks/useRewardConfig';
import { useCustomRewardBalance } from '../hooks/useCustomRewardBalance';
// import { useMultipleTokenBalances } from '../hooks/useMultipleTokenBalances';
import { useDerivedChainId } from '../hooks/useDerivedChainId';
import { useTilterBalances } from '../hooks/useTilterBalances';

export interface Contracts {
    coupon: Coupon;
    issuer: Issuer;
    inputs: ERC20[];
    hedgey: HedgeyAdapter
    uniswapRouter: UniswapV2Router02,
    uniswapFactory: UniswapV2Factory
    tilterFactory: TilterFactory
    tokenLockup: TokenLockupPlans
    multicall3: Multicall3
    uniPriceFetcher: UniPriceFetcher
    faucet?: TestnetFaucet
}

export interface TokenLockupConfig {
    threshold_size: number
    days_multiple: number,
    offset: number,
}

export const defaultLockup: TokenLockupConfig = { threshold_size: 0, days_multiple: 0, offset: 0 }


const defaultIsEth = (token: string) => false
const defaultIsTiltingToken = defaultIsEth

interface BlockchainContextType {
    accountIsOwner: boolean
    issuerIsMinter: boolean
    chainId: ChainID;
    contracts: Contracts | undefined;
    account: string | undefined
    selectedAssetId: string,
    rewardConfig: RewardConfig
    customRewardBalance: BigNumber
    flxDollarPrice: BigNumber,
    setSelectedAssetId: (assetId: string) => void
    dynamicTokenInfo: Record<string, DynamicTokenInfo> | undefined
    daiPriceOfEth: BigNumber | undefined
    tokenLockupConfig: TokenLockupConfig,
    inputDollarPrices: Record<string, BigNumber>
    refreshMultiCalls: () => void
    ethWindow?: EthWindow
    isEth: (token: string) => boolean
    isTiltingToken: (token: string) => boolean
    rewardTokenName: string,
    couponBalanceOfIssuer: BigNumber
    tilterBalanceMapping: Record<string, string>
}



const BlockchainContext = createContext<BlockchainContextType>({
    accountIsOwner: false, couponBalanceOfIssuer: BigNumber.from(0),
    chainId: ChainID.disconnected, contracts: {} as any, account: "0x0", selectedAssetId: '', flxDollarPrice: BigNumber.from('100000000000000000'),
    setSelectedAssetId: (id: string) => { }, dynamicTokenInfo: undefined, daiPriceOfEth: undefined,
    tokenLockupConfig: defaultLockup, refreshMultiCalls: () => { }, isEth: defaultIsEth, isTiltingToken: defaultIsTiltingToken, inputDollarPrices: {}, rewardConfig: defaultRewardConfig,
    customRewardBalance: BigNumber.from(0), rewardTokenName: '', issuerIsMinter: false, tilterBalanceMapping: {}
});

interface BlockchainProviderProps {
    children: ReactNode;
}

interface EthWindow {
    ethereum: any
}
interface EthGetBalance {
    (account: string): Promise<any>
}
export const BlockchainContextProvider: React.FC<BlockchainProviderProps> = ({ children }) => {

    const [selectedAssetId, setSelectedAssetId] = useState<string>('');
    const [refresh, setRefresh] = useState<number>(0)
    const { derivedChainId, account } = useDerivedChainId(setRefresh, refresh)
    const { addresses } = useAddresses(derivedChainId);

    const { contracts, accountIsOwner, couponBalanceOfIssuer, issuerIsMinter } = useContracts(addresses, account);
    const [getEthBalance, setGetEthBalance] = useState<EthGetBalance>()
    const isTiltingToken = isTiltingTokenFactory(derivedChainId)
    const dynamicTokenInfo = useDynamicTokenInfo(contracts, account, addresses, refresh)
    const pricedTokens = addresses ? [addresses.Coupon, addresses.Weth, ...addresses.Inputs] : []
    const inputDollarPrices = useInputPrices(contracts?.uniPriceFetcher, pricedTokens, refresh)

    const { flxDollarPrice, daiPriceOfEth } = useAnchorPrices(inputDollarPrices, addresses)
    const tokenLockupConfig = useTokenLockupConfig(contracts?.issuer)
    const rewardConfig = useRewardConfig(contracts?.issuer, refresh)
    const { customRewardBalance, rewardTokenName } = useCustomRewardBalance(addresses?.Issuer, rewardConfig.token, refresh)
    const tilterBalanceMapping = useTilterBalances(derivedChainId, contracts?.coupon, contracts?.tilterFactory, contracts?.multicall3, accountIsOwner);
    const ethWindow: EthWindow = (window as unknown) as EthWindow;

    // useEffect(() => {
    //     if (ethWindow.ethereum) {

    //         // (async () => {
    //         //     const result = await ethWindow.ethereum.request({ method: 'eth_getBalance', params: ['0x89091028D5097fF100D05De76f65DFC9631A9032', "latest"] })
    //         //     console.log('result :' + result)
    //         // })()
    //         console.log('setting')
    //         setGetEthBalance(async (currentAccount: string) => ethWindow.ethereum.request({ method: 'eth_getBalance', params: [currentAccount, "latest"] }))
    //     }
    // }, [ethWindow.ethereum])
    return (
        <BlockchainContext.Provider value={{
            couponBalanceOfIssuer,
            issuerIsMinter,
            accountIsOwner,
            chainId: derivedChainId,
            contracts,
            account,
            selectedAssetId,
            setSelectedAssetId,
            dynamicTokenInfo,
            flxDollarPrice,
            daiPriceOfEth,
            inputDollarPrices,
            rewardConfig,
            tokenLockupConfig,
            customRewardBalance,
            rewardTokenName,
            refreshMultiCalls: () => { console.log('Refreshed at ' + Date()); setTimeout(() => setRefresh(refresh + 1), 10000) },
            isEth: addresses ? (token: string) => token === addresses.Weth : defaultIsEth,
            isTiltingToken,
            ethWindow,
            tilterBalanceMapping
        }}>
            {children}
        </BlockchainContext.Provider>
    );
};



export const useBlockchainContext = () => useContext(BlockchainContext);

export const useInejctedBalance = (account: string | undefined, blockNumber: number | undefined) => {
    const { ethWindow } = useBlockchainContext()
    const [balance, setBalance] = useState<BigNumber | undefined>()

    useEffect(() => {
        if (ethWindow) {
            (async (
            ) => {
                const result = await ethWindow.ethereum.request({ method: 'eth_getBalance', params: [account, "latest"] })
                setBalance(BigNumber.from(result))

            })()
        }
    }, [ethWindow, blockNumber])
    return balance
}